import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowDownTrayIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { InvestigationAttachment } from '@/services/investigations.service';
import { getPreviewType, getFileTypeLabel, formatFileSize } from '@/utils/filePreview';
import { Button } from '@/components/ui/Button';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: InvestigationAttachment;
  downloadUrl?: string;
}

export default function FilePreviewModal({
  isOpen,
  onClose,
  attachment,
  downloadUrl,
}: FilePreviewModalProps) {
  const [textContent, setTextContent] = useState<string>('');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const previewType = getPreviewType(attachment.mimeType);
  const fileUrl = downloadUrl ?? `/api/v1/investigations/attachments/${attachment.id}/download`;

  // Get auth token for authenticated requests
  const getAuthHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('corp_security_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // Handle authenticated download
  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(fileUrl, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [fileUrl, attachment.originalFilename, getAuthHeaders]);

  // Fetch file with authentication and create blob URL for preview
  useEffect(() => {
    if (!isOpen) {
      // Clean up blob URL when modal closes
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setTextContent('');
      setLoadError(null);
      return;
    }

    // Don't fetch for unsupported preview types
    if (previewType === 'none') {
      return;
    }

    const fetchFile = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetch(fileUrl, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error('Failed to load file');
        }

        if (previewType === 'text') {
          // For text files, read as text
          const text = await response.text();
          setTextContent(text);
        } else {
          // For other file types, create a blob URL
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();

    // Cleanup function
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fileUrl, previewType, getAuthHeaders]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // VERACODE-MITIGATION: CWE-80 - React auto-escapes JSX output; no dangerouslySetInnerHTML used here.
  // Text content is rendered inside <pre> via JSX. Image/PDF/video sources use blob: URLs from
  // URL.createObjectURL, not user-controlled HTML strings.
  const renderPreview = () => {
    if (loadError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-danger-500 mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
            Failed to Load File
          </h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            {loadError}
          </p>
          <Button onClick={handleDownload}>
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Download File
          </Button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      );
    }

    switch (previewType) {
      case 'image':
        return blobUrl ? (
          <img
            src={blobUrl}
            alt={attachment.originalFilename}
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
            onError={() => setLoadError('Failed to load image')}
          />
        ) : null;

      case 'pdf':
        return blobUrl ? (
          <iframe
            src={blobUrl}
            title={attachment.originalFilename}
            className="w-full h-[70vh] rounded-lg border-0"
            sandbox="allow-scripts allow-same-origin"
            onError={() => setLoadError('Failed to load PDF')}
          />
        ) : null;

      case 'text':
        return (
          <div className="w-full max-h-[70vh] overflow-auto">
            <pre className="text-sm text-secondary-900 dark:text-secondary-100 bg-secondary-50 dark:bg-secondary-900 p-4 rounded-lg whitespace-pre-wrap break-words">
              {textContent}
            </pre>
          </div>
        );

      case 'video':
        return blobUrl ? (
          <video
            controls
            className="max-w-full max-h-[70vh] rounded-lg"
            onError={() => setLoadError('Failed to load video')}
          >
            <source src={blobUrl} type={attachment.mimeType} />
            Your browser does not support the video tag.
          </video>
        ) : null;

      case 'audio':
        return blobUrl ? (
          <div className="p-8">
            <audio
              controls
              className="w-full"
              onError={() => setLoadError('Failed to load audio')}
            >
              <source src={blobUrl} type={attachment.mimeType} />
              Your browser does not support the audio tag.
            </audio>
          </div>
        ) : null;

      default:
        return (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <ExclamationCircleIcon className="w-16 h-16 text-warning-500 mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Preview Not Available
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
              This file type cannot be previewed in the browser. Please download to view.
            </p>
            <Button onClick={handleDownload}>
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 cursor-pointer"
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-6xl bg-white dark:bg-secondary-800 rounded-lg shadow-2xl pointer-events-auto overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white truncate">
                    {attachment.originalFilename}
                  </h3>
                  <div className="flex items-center space-x-3 text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                    <span>{getFileTypeLabel(attachment.mimeType, attachment.evidenceType)}</span>
                    <span>•</span>
                    <span>{formatFileSize(attachment.fileSize)}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  <button
                    onClick={onClose}
                    className="p-2 text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 transition-colors"
                    aria-label="Close preview"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="p-6 flex items-center justify-center bg-secondary-50 dark:bg-secondary-900/50">
                {renderPreview()}
              </div>

              {/* Description */}
              {attachment.description && (
                <div className="p-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    <span className="font-medium">Description:</span> {attachment.description}
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
